import { createSelector } from 'reselect';

import Config from 'Config';

import * as ImagesClient from 'Clients/Images';
import RootState from "Store/Root";

import {
  ImageSettings,
  ViRSettings,
  ViRRenderItem
} from 'Types/Designs';

import { imageUrlSelector, getTopDownUrl } from './Urls';
import paramsSelector from './Params';
import { roomSelector } from './Params';
import { Props } from "./Props";

const HARDWOOD_RENDER_ITEM: ViRRenderItem = {
  surface: 'Floor',
  textureUrl: ImagesClient.Urls.Install.hardwood({ pixels: '20ppi' }),
  ppi: 20,
  repeatX: true,
  repeatY: true,
  rotate: 0
};

const emptyRoomInfo: TrykApi.Images.IRoomInfo = {
  roomId: 0,
  sceneWidth: 0,
  sceneHeight: 0,
  repeatSurface: '',
  repeatSurfaceQuality: undefined,
  corridorSurface: '',
  corridorSurfaceQuality: undefined,
  rugSurface: '',
  rugSurfaceQuality: undefined
};

const roomInfoSelector = createSelector(
  roomSelector,
  (state: RootState, props: Props) => state,
  (room: TrykApi.Catalog.IRoomScene, state: RootState): TrykApi.Images.IRoomInfo | undefined => {
    // If we have no room id or the room info is still loading, return undefined.
    if (!room?.roomId || state.rooms.roomInfoLoadingById[room?.roomId]) {
      return undefined;
    }

    // If we have requested the room info but got an error, return the empty room info.
    if (state.rooms.roomInfoErrorsById[room?.roomId]) {
      return emptyRoomInfo;
    }

    // If we have the room info, return it.
    const roomInfo = state.rooms.roomInfoById[room?.roomId];

    if (roomInfo) {
      return roomInfo;
    }

    // If we haven't requested the room info yet, return undefined.
    return undefined;
  }
);

const roomInfoLoadingSelector = createSelector(
  roomSelector,
  (state: RootState, props: Props) => state,
  (room: TrykApi.Catalog.IRoomScene, state: RootState): boolean => {
    if (!room?.roomId) {
      return false;
    }

    return state.rooms.roomInfoLoadingById[room.roomId];
  }
);

const virSettingsSelector = createSelector(
  paramsSelector,
  roomInfoSelector,
  roomInfoLoadingSelector,
  (params, roomInfo, roomInfoLoading): ViRSettings => {
    if (params.viewMode !== '3d' || !params.colorway || !params.room) {
      return null;
    } else if (!params.install || params.install.installId !== 1) {
      return null;
    } else if (!params.colorway) {
      return null;
    }

    const ppi = getTexturePPI(params.colorway.repeatSize);
    const textureUrl = getTopDownUrl({
      params,
      square: false,
      normalMap: false,
      pixels: `${ppi}ppi`
    });
    let normalUrl = getTopDownUrl({
      params,
      square: false,
      normalMap: true,
      pixels: `${ppi}ppi`
    });

    if (params.customRug && params.customRugVisible) {
      normalUrl = undefined;
    }

    const materialPropertiesUrl = ImagesClient.Urls.MaterialProperties.colorwayMaterialProperties(params.colorway.designCode, params.colorway.colorCode);

    const items: ViRRenderItem[] = [];

    if (params.sectionIds.some(x => x === 6)) {
      items.push({ ...HARDWOOD_RENDER_ITEM },
        {
          surface: 'Corridor',
          textureUrl,
          normalUrl,
          ppi,
          repeatX: false,
          repeatY: true,
          rotate: 0,
          materialPropertiesUrl
        });
    } else if (params.sectionIds.some(x => x === 7) ||
      (params.customRug && params.customRugVisible)) {
      items.push({ ...HARDWOOD_RENDER_ITEM },
        {
          surface: 'Rug',
          textureUrl,
          normalUrl,
          ppi,
          repeatX: false,
          repeatY: false,
          rotate: params.rotation,
          repeatSize: params.colorway.repeatSize,
        });
    } else if (params.sectionIds.some(x => x === 8) ||
      (params.customRug && params.customRugVisible)) {
      items.push({ ...HARDWOOD_RENDER_ITEM },
        {
          surface: 'Rug',
          textureUrl,
          normalUrl,
          ppi,
          repeatX: false,
          repeatY: false,
          rotate: params.rotation,
          materialPropertiesUrl,
          repeatSize: params.colorway.repeatSize,
        });
    } else {
      items.push({
        surface: 'Floor',
        textureUrl,
        normalUrl,
        ppi,
        repeatX: true,
        repeatY: true,
        rotate: params.rotation,
        materialPropertiesUrl
      });
    }

    return {
      roomId: params.room.roomId,
      items,
      roomInfo,
      roomInfoLoading,
    };
  }
);

const imageSettingsSelector = createSelector(
  paramsSelector,
  imageUrlSelector,
  virSettingsSelector,
  (params, imageUrl, virSettings): ImageSettings => {
    return {
      url: imageUrl,
      disableZoom: params.viewMode === '3d',
      disableContextMenu: Config.environment !== 'development',
      virSettings
    };
  }
);

export default imageSettingsSelector;

function getTexturePPI(repeatSize: TrykApi.Catalog.ITileSize): number {
  const maxPixelSize = 3750;
  const maxSizeInches = Math.max(repeatSize.widthInches, repeatSize.heightInches);

  let ppi = 72;

  if (maxSizeInches * ppi > maxPixelSize) {
    ppi = Math.floor(maxPixelSize / maxSizeInches);
  }

  return ppi;
}
